'use client';

import { useState } from 'react';

import Link from 'next/link';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  RequestPasswordResetRequest,
  RequestPasswordResetStatus,
  type RequestPasswordResetResponse,
} from '@/lib/api';
import { API_URL } from '@/lib/consts';
import { unsupportedEmailReasonMessage } from '@/lib/emailUnsupportedReasonMessage';

enum RequestState {
  Initial,
  Sending,
  Success,
  Error,
}

export default function RequestPasswordResetPage() {
  const [requestState, setRequestState] = useState<RequestState>(RequestState.Initial);
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setRequestState(RequestState.Sending);
    setErrorMessage('');

    const formData = new FormData(event.currentTarget);
    const emailValue = (formData.get('email') as string).trim();

    try {
      const res = await fetch(`${API_URL}/auth/request_password_reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: emailValue,
        } satisfies RequestPasswordResetRequest),
      });

      if (res.ok) {
        const data = (await res.json()) as RequestPasswordResetResponse;
        if (data.accepted && data.status === RequestPasswordResetStatus.Accepted) {
          setRequestState(RequestState.Success);
        } else if (!data.accepted && data.status === RequestPasswordResetStatus.UnsupportedFormat) {
          setErrorMessage(unsupportedEmailReasonMessage(data.reason));
          setRequestState(RequestState.Error);
        } else {
          setErrorMessage('Request failed. Please try again.');
          setRequestState(RequestState.Error);
        }
      } else {
        setErrorMessage('An error occurred. Please try again.');
        setRequestState(RequestState.Error);
      }
    } catch (error) {
      console.error('Password reset request error:', error);
      setErrorMessage('Network error. Please try again.');
      setRequestState(RequestState.Error);
    }
  };

  if (requestState === RequestState.Success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Check your email
        </Typography>
        <Alert severity="success" sx={{ mt: 2 }}>
          If an account exists with that email address, we&apos;ve sent password reset instructions.
        </Alert>
        <Typography variant="body1" sx={{ mt: 2 }}>
          Didn&apos;t receive an email?{' '}
          <Button
            variant="text"
            onClick={() => setRequestState(RequestState.Initial)}
            sx={{ p: 0, minWidth: 'auto' }}
          >
            Try again
          </Button>
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          <Link href="/login">Back to login</Link>
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Reset your password
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Enter your email address and we&apos;ll send you a link to reset your password.
      </Typography>

      <Box component="form" method="POST" onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            id="email"
            name="email"
            label="Email address"
            type="email"
            required
            fullWidth
            variant="outlined"
            aria-required="true"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={requestState === RequestState.Sending}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={requestState === RequestState.Sending}
            aria-disabled={requestState === RequestState.Sending}
          >
            {requestState === RequestState.Sending ? 'Sending...' : 'Send reset link'}
          </Button>

          {requestState === RequestState.Error && errorMessage && (
            <Alert severity="error" aria-live="assertive" aria-atomic="true">
              {errorMessage}
            </Alert>
          )}

          <Typography variant="body1" sx={{ textAlign: 'center' }}>
            <Link href="/login">Back to login</Link>
          </Typography>
        </Stack>
      </Box>
    </Container>
  );
}
